import { changelogApi } from 'src/services/http';
import {
	type BenefitCellValue,
	type BenefitItem,
	type BenefitsCategory,
	type BenefitsList,
	type ChangelogVersionMeta,
	type ReleaseFeature,
	type ReleaseVersion,
} from 'src/types/Changelog';
import { LANG_DICT } from 'src/constants';

const VERSIONS_PATH = 'api/v1/release-notes/versions';
const VERSION_PATH = 'api/v1/release-notes/version';
const BENEFITS_PATH = 'api/v1/benefits/all';

const benefitsCache = new Map<string, BenefitsList>();

/**
 * On initial load the svelte-i18n locale can be the full browser tag
 * (e.g. "en-US") before setUpI18n() resolves it to a supported key.
 * Fall back to the base language when the full tag isn't in LANG_DICT.
 */
const normalizeLocale = (locale: string): string => {
	if (locale in LANG_DICT) return locale;
	const base = locale.split('-')[0];
	return base in LANG_DICT ? base : 'en';
};

const unwrapBody = (res: any) => (res?.success !== undefined ? res : res?.data);

const toTimestamp = (date: string): number => Math.floor(new Date(date).getTime() / 1000);

export const fetchChangelogVersions = async (): Promise<ChangelogVersionMeta[]> => {
	const res: any = await changelogApi.get(VERSIONS_PATH);
	const body = unwrapBody(res);

	if (!body?.success || !Array.isArray(body.data)) return [];

	const threshold = Math.floor(Date.now() / 1000);

	return body.data
		.map((v: any) => ({
			appVersion: v.appVersion,
			date: v.date,
			timestamp: toTimestamp(v.date),
		}))
		.filter((v: ChangelogVersionMeta) => v.timestamp < threshold)
		.sort((a: ChangelogVersionMeta, b: ChangelogVersionMeta) => b.timestamp - a.timestamp);
};

export const fetchChangelogVersion = async (
	appVersion: string,
	locale: string,
): Promise<ReleaseVersion | null> => {
	const res: any = await changelogApi.get(VERSION_PATH, {
		params: { appVersion, locale: normalizeLocale(locale) },
	});
	const body = unwrapBody(res);

	if (!body?.success || !body.data) return null;

	const data = body.data;

	return {
		timestamp: toTimestamp(data.date),
		appVersion: data.appVersion,
		features: (data.features ?? []).map((f: any): ReleaseFeature => ({
			type: f.type,
			title: f.translation?.title ?? '',
			description: f.translation?.description ?? '',
			premium: !!f.premium,
			experimental: !!f.experimental,
		})),
	};
};

const normalizeBenefitValue = (value: any): BenefitCellValue => {
	const type = String(value?.type ?? 'cross').toLowerCase();

	if (type === 'check' || type === 'text') {
		return { type, label: value?.label };
	}

	return { type: 'cross', label: value?.label };
};

/**
 * Fetches a CMS-managed subscription benefits comparison list.
 * Cached per locale + category for the session.
 */
export const fetchBenefits = async (
	locale: string,
	category: BenefitsCategory,
): Promise<BenefitsList | null> => {
	const normalizedLocale = normalizeLocale(locale);
	const cacheKey = `${normalizedLocale}|${category}`;
	const cached = benefitsCache.get(cacheKey);

	if (cached) return cached;

	const res: any = await changelogApi.get(BENEFITS_PATH, {
		params: { locale: normalizedLocale, category },
	});
	const body = unwrapBody(res);

	if (!body?.success || !Array.isArray(body.data) || body.data.length === 0) {
		return null;
	}

	const list =
		body.data.find((entry: { category?: string }) => entry?.category === category) ?? body.data[0];

	if (!list || !Array.isArray(list.items)) return null;

	const items: BenefitItem[] = [...list.items]
		.filter((item: any) => item?.translation)
		.sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
		.map((item: any): BenefitItem => ({
			sortOrder: item.sortOrder ?? 0,
			badges: Array.isArray(item.badges) ? item.badges : [],
			standardValue: normalizeBenefitValue(item.standardValue),
			featuredValue: normalizeBenefitValue(item.featuredValue),
			translation: {
				title: item.translation?.title ?? '',
				description: item.translation?.description ?? '',
				standardValueLabel: item.translation?.standardValueLabel ?? null,
				featuredValueLabel: item.translation?.featuredValueLabel ?? null,
			},
		}));

	if (items.length === 0) return null;

	const result: BenefitsList = {
		category: list.category,
		standardColumnLabel: list.standardColumnLabel ?? '',
		featuredColumnLabel: list.featuredColumnLabel ?? '',
		items,
	};

	benefitsCache.set(cacheKey, result);

	return result;
};
